/**
 * The inline citation marker component (dagger + hover tooltip + click
 * excerpt popover), used by `renderInline` (`./inlineCitation`). Split into
 * its own component-only module so that helper file can export a plain
 * function (`renderInline`) without mixing component/non-component exports.
 */
import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import apiUtils, { isApiError } from "../../apiUtils";
import { ASSESSMENT_PREFIX } from "./cardRanking";
import { revealTargetFromRef } from "./citationResolve";
import { useRevealSource } from "../RevealSourceContext";
import type { BlueprintDocumentResponse, SourceCitation } from "../../types";

/** Viewport-relative coordinates for positioning a portaled popover. */
interface PopoverPosition {
  top: number;
  left: number;
}

/**
 * Computes where to render the portaled popover, anchored under the given
 * trigger element in viewport coordinates (the popover is fixed-positioned
 * in `document.body`, escaping any ancestor `overflow: hidden`/`auto`).
 *
 * @param anchor - The citation marker element the popover hangs off of.
 * @returns The `top`/`left` viewport coordinates for the popover.
 */
function computePopoverPosition(anchor: HTMLElement): PopoverPosition {
  const rect = anchor.getBoundingClientRect();
  return { top: rect.bottom + 4, left: rect.left + rect.width / 2 };
}

/**
 * Extracts 1-2 lines before and after the cited lines from a document body to
 * serve as a fallback excerpt when no structured snippet is present.
 */
function extractSurroundingExcerpt(body: string, lines: number[]): string {
  if (!lines || lines.length === 0) return "";
  const allLines = body.split("\n");
  const minLine = Math.max(1, Math.min(...lines));
  const maxLine = Math.min(allLines.length, Math.max(...lines));
  const startIdx = Math.max(0, minLine - 2); // 1-2 lines before
  const endIdx = Math.min(allLines.length, Math.max(startIdx + 3, maxLine + 1));
  return allLines.slice(startIdx, endIdx).join("\n").trim();
}

/**
 * Extracts the enclosing paragraph for a cited snippet or line range from a document body.
 */
function extractEnclosingParagraph(
  body: string,
  snippet: string,
  lines: number[],
): string {
  if (!body) return "";
  const paragraphs = body.split(/\r?\n\s*\r?\n/);
  const trimmed = snippet.trim();
  if (trimmed) {
    for (const para of paragraphs) {
      if (para.includes(trimmed)) {
        return para.trim();
      }
    }
  }
  if (lines && lines.length > 0) {
    const allLines = body.split(/\r?\n/);
    const targetIdx = lines[0] - 1;
    if (targetIdx >= 0 && targetIdx < allLines.length) {
      if (allLines[targetIdx].trim() !== "") {
        let startIdx = targetIdx;
        let endIdx = targetIdx;
        while (startIdx > 0 && allLines[startIdx - 1].trim() !== "") {
          startIdx--;
        }
        while (
          endIdx < allLines.length - 1 &&
          allLines[endIdx + 1].trim() !== ""
        ) {
          endIdx++;
        }
        return allLines
          .slice(startIdx, endIdx + 1)
          .join("\n")
          .trim();
      }
    }
  }
  return extractSurroundingExcerpt(body, lines);
}

/**
 * Renders an expanded paragraph with the cited snippet highlighted.
 */
function renderHighlightedParagraph(
  paragraph: string,
  snippet: string,
): React.ReactElement {
  const trimmed = snippet.trim();
  const idx = trimmed ? paragraph.indexOf(trimmed) : -1;
  if (idx === -1) {
    return <span>{paragraph}</span>;
  }
  const before = paragraph.slice(0, idx);
  const match = paragraph.slice(idx, idx + trimmed.length);
  const after = paragraph.slice(idx + trimmed.length);
  return (
    <>
      {before && <span className="text-slate-500 font-normal">{before}</span>}
      <mark className="text-blue-950 font-semibold bg-blue-100/80 px-1 py-0.5 rounded border border-blue-200 select-text">
        {match}
      </mark>
      {after && <span className="text-slate-500 font-normal">{after}</span>}
    </>
  );
}

/**
 * The click-popover: the cited excerpt + a "Go to source" navigate button.
 *
 * Surfaces the verbatim cited text inline (feature: excerpt popout) so the
 * provider can verify a citation without leaving the card, and a button that
 * routes to the source, scrolls to the cited span, and flashes it (feature:
 * navigate + highlight). When surrounding context (`context_before` /
 * `context_after`) is present, renders a tripartite context window around
 * the highlighted snippet. Supports expanding the excerpt to the full enclosing
 * paragraph via an "Expand to paragraph" toggle. When no structured snippet
 * exists in citation metadata, the popover checks for an embedded verbatim
 * quote in the citation ref (rendering synchronously with zero latency) before
 * falling back to dynamically loading the source document to extract
 * surrounding lines around the cited target. The raw source id is never shown.
 *
 * Rendered through a `document.body` portal, fixed-positioned off the
 * trigger's live bounding rect, so it always floats above any ancestor
 * card's `overflow: hidden`/`auto` instead of being clipped by it.
 *
 * Dismisses on click-outside (excluding the trigger anchor) or pressing Escape.
 *
 * @param props - The label, the resolved citation (if any), the raw ref, the
 *   trigger element to anchor against, and a close handler.
 * @returns The portaled popover element.
 */
function CitationPopover({
  label,
  citation,
  refValue,
  anchor,
  onClose,
}: {
  label: string;
  citation?: SourceCitation;
  refValue: string;
  anchor: HTMLElement;
  onClose: () => void;
}): React.ReactElement {
  const revealSource = useRevealSource();
  const popoverRef = useRef<HTMLSpanElement | null>(null);
  const quoteMatch = /:\s*"([^"]+)"/.exec(refValue);
  const embeddedQuote = quoteMatch ? quoteMatch[1].trim() : "";
  const initialSnippet = citation?.snippet?.trim() || embeddedQuote;
  const [fetchedSnippet, setFetchedSnippet] = useState<string>("");
  const snippet = initialSnippet !== "" ? initialSnippet : fetchedSnippet;

  const contextBefore = citation?.context_before?.trim() || "";
  const contextAfter = citation?.context_after?.trim() || "";
  const hasSurroundingContext = contextBefore !== "" || contextAfter !== "";

  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [expandedParagraph, setExpandedParagraph] = useState<string>("");
  const [loadingParagraph, setLoadingParagraph] = useState<boolean>(false);

  const [position, setPosition] = useState<PopoverPosition>(() =>
    computePopoverPosition(anchor),
  );

  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const reposition = (): void => setPosition(computePopoverPosition(anchor));
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [anchor]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (
        target instanceof Node &&
        !popoverRef.current?.contains(target) &&
        !anchor.contains(target)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchor, onClose]);

  useEffect(() => {
    if (initialSnippet !== "" || refValue.startsWith(ASSESSMENT_PREFIX)) {
      return;
    }
    const target = revealTargetFromRef(refValue, citation);

    let cancelled = false;
    const fetchSource = async (): Promise<void> => {
      try {
        const resp = await apiUtils.fetchData<BlueprintDocumentResponse>(
          `/api/documents/${encodeURIComponent(target.sourceId)}`,
        );
        if (isApiError(resp) || cancelled) return;
        const bodyContent = resp.current_version?.content?.body ?? "";
        const excerpt = extractSurroundingExcerpt(bodyContent, target.lines);
        if (excerpt && !cancelled) {
          setFetchedSnippet(excerpt);
        }
      } catch {
        // Fall back gracefully to prompt
      }
    };
    void fetchSource();
    return () => {
      cancelled = true;
    };
  }, [initialSnippet, refValue, citation]);

  const handleToggleExpand = async (): Promise<void> => {
    if (isExpanded) {
      setIsExpanded(false);
      return;
    }
    if (expandedParagraph !== "") {
      setIsExpanded(true);
      return;
    }
    const target = revealTargetFromRef(refValue, citation);
    setLoadingParagraph(true);
    try {
      const resp = await apiUtils.fetchData<BlueprintDocumentResponse>(
        `/api/documents/${encodeURIComponent(target.sourceId)}`,
      );
      if (!isMountedRef.current) return;
      if (!isApiError(resp)) {
        const bodyContent = resp.current_version?.content?.body ?? "";
        const para = extractEnclosingParagraph(
          bodyContent,
          snippet,
          target.lines,
        );
        if (para && isMountedRef.current) {
          setExpandedParagraph(para);
          setIsExpanded(true);
        }
      }
    } catch {
      // Degrade gracefully
    } finally {
      if (isMountedRef.current) {
        setLoadingParagraph(false);
      }
    }
  };

  const goToSource = (): void => {
    revealSource(revealTargetFromRef(refValue, citation));
    onClose();
  };

  return createPortal(
    <span
      ref={popoverRef}
      role="dialog"
      aria-label={label}
      data-testid="citation-popover"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        transform: "translateX(-50%)",
      }}
      className="z-50 block w-88 sm:w-96 rounded-lg border border-slate-200 bg-white p-2.5 text-left text-[0.7rem] font-normal text-slate-700 shadow-xl"
    >
      <span className="mb-1 block font-medium text-slate-500">{label}</span>
      {snippet.trim() !== "" ? (
        <blockquote
          data-testid="citation-excerpt"
          className="mb-2 block max-h-48 overflow-auto whitespace-pre-wrap rounded border-l-2 border-blue-400 bg-slate-50/70 p-2 text-slate-700 font-normal leading-relaxed text-[0.72rem]"
        >
          {isExpanded && expandedParagraph ? (
            renderHighlightedParagraph(expandedParagraph, snippet)
          ) : hasSurroundingContext ? (
            <>
              {contextBefore && (
                <span className="text-slate-400 font-normal select-text">
                  {contextBefore}{" "}
                </span>
              )}
              <mark className="text-blue-950 font-semibold bg-blue-100/80 px-1 py-0.5 rounded border border-blue-200 select-text">
                {snippet}
              </mark>
              {contextAfter && (
                <span className="text-slate-400 font-normal select-text">
                  {" "}
                  {contextAfter}
                </span>
              )}
            </>
          ) : (
            <span className="italic text-slate-600">{snippet}</span>
          )}
        </blockquote>
      ) : (
        <span className="mb-2 block text-slate-400 italic">
          Open the source to view the cited lines.
        </span>
      )}
      {/* A live assessment score has no document to open — the record IS the
          excerpt above. Offering "Go to source" would navigate to a source
          viewer that has nothing to show, so it is omitted rather than left
          as a dead affordance. */}
      {refValue.startsWith(ASSESSMENT_PREFIX) ? (
        <span
          data-testid="citation-record-note"
          className="block text-slate-400"
        >
          Live assessment record — not a document.
        </span>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="citation-goto"
            onClick={goToSource}
            className="rounded bg-blue-600 px-2 py-0.5 text-[0.7rem] font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400"
          >
            Go to source
          </button>
          <button
            type="button"
            data-testid="citation-expand-toggle"
            onClick={() => void handleToggleExpand()}
            disabled={loadingParagraph}
            className="rounded px-2 py-0.5 text-[0.7rem] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-slate-400 disabled:opacity-50"
          >
            {loadingParagraph
              ? "Loading…"
              : isExpanded
                ? "Collapse"
                : "Expand to paragraph"}
          </button>
        </div>
      )}
    </span>,
    document.body,
  );
}

/**
 * The click-popover for an unresolved citation.
 * Rendered through a `document.body` portal, fixed-positioned off the
 * trigger's live bounding rect.
 *
 * Dismisses on click-outside (excluding the trigger anchor) or pressing Escape.
 */
function UnresolvedCitationPopover({
  tagText,
  anchor,
  onClose,
}: {
  tagText: string;
  anchor: HTMLElement;
  onClose: () => void;
}): React.ReactElement {
  const popoverRef = useRef<HTMLSpanElement | null>(null);
  const [position, setPosition] = useState<PopoverPosition>(() =>
    computePopoverPosition(anchor),
  );
  useEffect(() => {
    const reposition = (): void => setPosition(computePopoverPosition(anchor));
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [anchor]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent): void => {
      const target = event.target;
      if (
        target instanceof Node &&
        !popoverRef.current?.contains(target) &&
        !anchor.contains(target)
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchor, onClose]);

  return createPortal(
    <span
      ref={popoverRef}
      role="dialog"
      aria-label={`Unresolved citation: ${tagText}`}
      data-testid="citation-popover-unresolved"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        transform: "translateX(-50%)",
      }}
      className="z-50 block w-56 rounded-lg border border-slate-200 bg-white p-2 text-left text-[0.7rem] font-normal text-slate-700 shadow-xl"
    >
      <span className="mb-1 block font-medium text-slate-500">
        Unresolved citation
      </span>
      <span
        data-testid="citation-unresolved-tag"
        className="block text-slate-600"
      >
        {tagText}
      </span>
    </span>,
    document.body,
  );
}

/**
 * The unresolved-citation dagger: the honest, text-only sibling of
 * {@link Citation}.
 *
 * An imported profile's citation tags (e.g. `NPE_54740`) do not resolve to a
 * PCP source in Phase 1 (no encounter join key persisted yet — ADR-054). The
 * captain's two rulings both hold here: the incoming tag text stays
 * REACHABLE (hover label + click popover, never discarded), and it links
 * only when resolvable — so this marker never offers a "Go to source"
 * affordance, and is styled in neutral slate (not the citation blue) so it
 * cannot be mistaken for a working link.
 *
 * @param props - The raw, comma-joined tag text to keep reachable.
 * @returns The unresolved dagger marker element.
 */
export function UnresolvedCitation({
  tagText,
}: {
  tagText: string;
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [markerEl, setMarkerEl] = useState<HTMLElement | null>(null);
  const tipId = useId();
  const label = `Unresolved citation: ${tagText}`;
  return (
    <span className="relative inline-block align-baseline">
      <sup
        ref={setMarkerEl}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={shown && !open ? tipId : undefined}
        title={label}
        data-testid="citation-marker-unresolved"
        onMouseEnter={() => setShown(true)}
        onMouseLeave={() => setShown(false)}
        onFocus={() => setShown(true)}
        onBlur={() => setShown(false)}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        className="ml-0.5 cursor-pointer rounded text-[0.65em] font-medium text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-slate-400"
      >
        †
      </sup>
      {shown && !open && (
        <span
          id={tipId}
          role="tooltip"
          data-testid="citation-tooltip-unresolved"
          className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[0.7rem] font-normal text-white shadow-lg"
        >
          {label}
        </span>
      )}
      {open && markerEl && (
        <UnresolvedCitationPopover
          tagText={tagText}
          anchor={markerEl}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

/**
 * A subtle inline citation marker with a hover tooltip + click excerpt popover.
 *
 * Renders a superscript dagger that, on hover/focus, reveals a friendly
 * citation label (e.g. "Source: Visit 1 Transcript, lines 3–7"), and on **click**
 * opens a popover showing the verbatim cited excerpt + a "Go to source" button
 * (scroll-to + flash in the source viewer). The raw source id is never surfaced
 * — only a clinician-facing label + line range. Keyboard-focusable and labelled
 * for accessibility; nothing PHI is logged.
 *
 * @param props - The pre-built clinician-facing label, the raw ref, and the
 *   resolved structured citation (explicit lines + snippet) for this ref.
 * @returns The citation affordance element.
 */
export function Citation({
  value,
  label,
  citation,
}: {
  value: string;
  label: string;
  citation?: SourceCitation;
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);
  const [markerEl, setMarkerEl] = useState<HTMLElement | null>(null);
  const tipId = useId();
  return (
    <span className="relative inline-block align-baseline">
      <sup
        ref={setMarkerEl}
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={shown && !open ? tipId : undefined}
        title={label}
        data-testid="citation-marker"
        onMouseEnter={() => setShown(true)}
        onMouseLeave={() => setShown(false)}
        onFocus={() => setShown(true)}
        onBlur={() => setShown(false)}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        className="ml-0.5 cursor-pointer text-[0.65em] font-medium text-blue-500 hover:text-blue-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-400 rounded"
      >
        †
      </sup>
      {shown && !open && (
        <span
          id={tipId}
          role="tooltip"
          data-testid="citation-tooltip"
          className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[0.7rem] font-normal text-white shadow-lg"
        >
          {label}
        </span>
      )}
      {open && markerEl && (
        <CitationPopover
          label={label}
          citation={citation}
          refValue={value}
          anchor={markerEl}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}
